import { useQuery } from '@tanstack/react-query';
import { atom } from 'jotai';
import { useRouter } from 'next/router';

import { fetchByKba, fetchByPlateOrVin } from '../../services/vehicles';
import getUserSavedCars from '../../utils/react-query/query-options/get-user-saved-cars';
import useToken from '../use-token';
import { ReactNode } from 'react';

const regularExpression = /^\w{4}-\w{3}$/;

export const loadVehicles = async ({ locale, slug, isKba }) => {
  if (isKba) {
    if (slug.match(regularExpression)) {
      const [hsn, tsn] = slug.split('-');
      return fetchByKba({ locale, hsn, tsn });
    }
    return;
  }

  return fetchByPlateOrVin({ locale, slug });
};

/*export const brandAtom = atom(
  !!process.env.NEXT_PUBLIC_BRAND_SLUG
    ? { value: process.env.NEXT_PUBLIC_BRAND_SLUG }
    : null
);*/

export const brandAtom = atom(null);

export const modelAtom = atom<TModel | { slug: string } | null>(null);
export const versionAtom = atom<TVersion | { slug: string } | null>(null);
export const availableVersionsAtom = atom<TVersion[] | null>(null);
export const insertedPlateAtom = atom<string | undefined>(undefined);

import { atomWithStorage } from 'jotai/utils';

export type TSearchedPlateData = {
  plate?: string;
  vin?: string;
  engine_code?: string;
  searchSlug?: string;
  vehicleSlugs?: string[];
} | null;

export const searchedPlateDataAtom = atomWithStorage<TSearchedPlateData>('bparts_plate_search_data', null);

export const searchByPlateOrKBAAtom = atom<boolean>(false);
export const showTakataMessageAtom = atom<boolean>(false);

export const slugAtom = atom((get) => {
  const model = get<TModel | null>(modelAtom);
  const version = get(versionAtom);

  if (model && !model.have_versions) {
    return model.slug;
  }

  if (version) {
    return version?.value;
  }

  return null;
});

export const setBrandAtom = atom(null, (get, set, value) =>
  set(brandAtom, value)
);
export const setModelAtom = atom(null, (get, set, value) =>
  set(modelAtom, value)
);
export const setversionAtom = atom(null, (get, set, value) =>
  set(versionAtom, value)
);

export const resetPickerSelection = atom(null, (get, set) => {
  set(
    brandAtom,
    !!process.env.NEXT_PUBLIC_BRAND_SLUG
      ? { value: process.env.NEXT_PUBLIC_BRAND_SLUG }
      : null
  );
  set(modelAtom, null);
  set(versionAtom, null);
});

export const sendEvent = (plate: string) => {
  window.dataLayer.push({
    event: 'plateSearch',
    plateNumber: plate, // car plate
  });
};

export const useGetVehicles = () => {
  const { locale } = useRouter();
  const csrftoken = useToken();

  const { data: vehicles, isLoading } = useQuery(
    ...getUserSavedCars({ locale, csrftoken }),
    {
      refetchOnMount: 'always',
      keepPreviousData: true,
      refetchOnWindowFocus: 'always',
      refetchInterval: 60000,
      staleTime: 10000,
    }
  );
  return { vehicles, isLoading };
};

export const partAtom = atom(null);
export const scheduleAtom = atom('15d');
export const referenceAtom = atom(null);
export const availabilityErrorAtom = atom<boolean>(false);

export const vinAtom = atom<string | null>(null);
export const plateOrKBAAtom = atom<string | null>(null);
export const engineCodeAtom = atom<string | null>(null);
export const multiPartsAtom = atom<string[]>([]);
// Set once the parts are cleared or changed without searching yet. While true,
// `multiPartsAtom` — not the URL, which still lists the previously searched
// parts — is the live selection, so the drawer must not re-hydrate itself from
// the URL every time it reopens. Reset once a search actually runs.
export const partsTouchedAtom = atom<boolean>(false);
// Pending (typed but not yet searched) value of the reference input. `null`
// means it wasn't touched, so the `reference` already in the URL still stands;
// `''` means it was cleared. Editing or clearing it only takes effect on the
// next search, so every submit path — including the parts drawer, which
// rebuilds the query from the router — has to honour this instead of
// resurrecting the last searched value.
export const pendingReferenceAtom = atom<string | null>(null);
